/**
 * HearingReportHistoryModal — lists all saved hearing-report snapshots for
 * a patient, most-recent first. Clicking a row opens the read-only viewer.
 *
 * Props:
 *   open       : boolean
 *   patientId  : string
 *   patientName: string (for the modal header)
 *   sessionId  : string  (highlights versions saved from the CURRENT session)
 *   onClose    : ()   → void
 *
 * The list stays lightweight — the heavy `snapshot` blob is fetched only
 * when the audiologist opens a specific version.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, FileText, Loader2, Eye, Pencil, Trash2 } from 'lucide-react';
import HearingReportViewerModal from './HearingReportViewerModal';
import HearingReportEditModal from './HearingReportEditModal';

import useEscapeKey from '../hooks/useEscapeKey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(iso).slice(0, 16); }
};

export default function HearingReportHistoryModal({ open, patientId, patientName, sessionId, onClose }) {
  useEscapeKey(onClose);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [viewerId, setViewerId] = useState(null); // version_id being previewed
  const [editorId, setEditorId] = useState(null); // version_id being edited
  const [deleteTarget, setDeleteTarget] = useState(null); // {version_id, label}
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/hearing-reports/patient/${encodeURIComponent(patientId)}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load history');
    } finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.version_id || deleting) return;
    setDeleting(true); setDeleteErr('');
    try {
      await axios.delete(`${API}/hearing-reports/${deleteTarget.version_id}`);
      setDeleteTarget(null);
      // Refresh the list so the deleted row disappears immediately.
      load();
    } catch (e) {
      setDeleteErr(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
        data-testid="hearing-report-history-modal"
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        >
          {/* Header */}
          <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-cyan-600" />
              <div>
                <h2 className="text-sm font-bold text-slate-900">Saved Reports</h2>
                <div className="text-[11px] text-slate-500">{patientName || 'Patient'}</div>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="hearing-report-history-close"
              className="w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
            >
              <X size={16} />
            </button>
          </header>

          {/* List */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={20} className="animate-spin text-slate-400" />
              </div>
            ) : err ? (
              <div className="p-8 text-center text-sm text-rose-600 font-semibold">{err}</div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center">
                <FileText size={28} className="mx-auto text-slate-300 mb-2" />
                <div className="text-sm font-semibold text-slate-700">No saved reports yet</div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Click <b>SAVE</b> on the Reports tab to keep a version of this visit.
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <li
                    key={r.version_id}
                    data-testid={`hearing-report-history-row-${i}`}
                    className={`px-5 py-3 hover:bg-cyan-50/50 flex items-center justify-between gap-3 ${r.session_id === sessionId ? 'bg-emerald-50/40' : ''}`}
                  >
                    <div
                      className="min-w-0 flex-1 cursor-pointer"
                      onClick={() => setViewerId(r.version_id)}
                    >
                      <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-2">
                        {r.label}
                        {r.session_id === sessionId && (
                          <span className="text-[9px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                            This visit
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Saved {fmtWhen(r.saved_at)}
                        {r.saved_by_name ? ` by ${r.saved_by_name}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setViewerId(r.version_id); }}
                        data-testid={`hearing-report-history-view-${i}`}
                        title="Open saved report (read-only)"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                      >
                        <Eye size={11} /> View
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setEditorId(r.version_id); }}
                        data-testid={`hearing-report-history-edit-${i}`}
                        title="Re-open the report builder for this saved version"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                      >
                        <Pencil size={11} /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget({ version_id: r.version_id, label: r.label }); }}
                        data-testid={`hearing-report-history-delete-${i}`}
                        title="Delete this saved report (confirmation required)"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <footer className="px-5 py-2.5 border-t border-slate-100 text-[10px] text-slate-400">
            Snapshots are stored as JSON — the report re-renders exactly as it was saved.
          </footer>
        </div>
      </div>

      {viewerId && (
        <HearingReportViewerModal
          versionId={viewerId}
          onClose={() => setViewerId(null)}
        />
      )}

      {editorId && (
        <HearingReportEditModal
          versionId={editorId}
          onClose={() => setEditorId(null)}
          onSaved={() => { load(); }}
        />
      )}

      {deleteTarget && (
        <div
          className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4"
          data-testid="hearing-report-delete-confirm"
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 size={18} className="text-rose-600" />
              <h3 className="text-sm font-bold text-slate-900">Delete saved report?</h3>
            </div>
            <p className="text-sm text-slate-700 mb-1">
              This will permanently remove
              <span className="font-semibold text-slate-900"> {deleteTarget.label}</span>.
            </p>
            <p className="text-xs text-slate-500 mb-4">
              The underlying test session and audiogram data will stay intact — only this saved snapshot is removed.
            </p>
            {deleteErr && (
              <div
                className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 mb-2"
                data-testid="hearing-report-delete-error"
              >{deleteErr}</div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => { setDeleteTarget(null); setDeleteErr(''); }}
                disabled={deleting}
                data-testid="hearing-report-delete-cancel"
                className="text-xs font-semibold px-3 py-1.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              >Cancel</button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                data-testid="hearing-report-delete-confirm-btn"
                className="text-xs font-semibold px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
              >{deleting ? 'Deleting…' : 'Yes, delete'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
